'use client'
import React, { createContext, useContext, useState, useCallback, useMemo, useEffect } from 'react'
import { login as authLogin, logout as authLogout, validateAndRefreshToken } from '@/services/auth'
import { isRedirectingToLogin, redirectToLogin } from '@/services/api'
import { areAllTokensInvalid } from '@/services/token'
import { useUser } from './UserContext'
import { useError } from './ErrorContext'
import { AuthContextType } from '@/types/auth'
import { AuthProviderProps } from '@/types/contexts'

const AuthContext = createContext<AuthContextType | null>(null)

/**
 * 認証コンテキストを使用するためのカスタムフック
 * @throws Error コンテキストがProviderの外部で使用された場合
 */
export const useAuth = () => {
  const context = useContext(AuthContext)
  if (!context) {
    throw new Error('useAuth must be used within an AuthProvider')
  }
  return context
}

/**
 * 認証状態を管理し、子コンポーネントに提供するプロバイダーコンポーネント
 */
export const AuthProvider = ({ children }: AuthProviderProps) => {
  const [isLoggedIn, setIsLoggedIn] = useState(false)
  const { refreshUserInfo, clearUser } = useUser()
  const { handleError } = useError()

  /**
   * 認証状態を確認し、必要に応じてトークンを更新する
   * トークンが有効な場合はユーザー情報を更新し、無効な場合はログアウト状態にする
   */
  const checkAuth = useCallback(async () => {
    try {
      const isValid = await validateAndRefreshToken();
      setIsLoggedIn((prevIsLoggedIn) => {
        // 前回ログイン状態だったが今回無効になった場合
        if (prevIsLoggedIn && !isValid && !isRedirectingToLogin()) {
          handleError('AUTH_EXPIRED');
          // トークンが全て無効な場合はログイン画面へリダイレクト
          if (areAllTokensInvalid()) {
            redirectToLogin();
          }
        }
        // 値が変わらない場合は同じ参照を返して再レンダリングを防止
        if (prevIsLoggedIn === isValid) return prevIsLoggedIn;
        return isValid;
      });
      if (isValid) {
        await refreshUserInfo();
      } else {
        clearUser();
      }
    } catch (error) {
      setIsLoggedIn((prevIsLoggedIn) => {
        // 前回ログイン状態だった場合のみエラー表示（リダイレクト中は除く）
        if (prevIsLoggedIn && !isRedirectingToLogin()) {
          const errorCode = error instanceof Error && error.message === 'AUTH_EXPIRED'
            ? 'AUTH_EXPIRED'
            : 'AUTH_REFRESH_FAILED';
          handleError(errorCode);
          redirectToLogin();
        }
        return false;
      });
      clearUser();
    }
  }, [refreshUserInfo, clearUser, handleError]);

  /**
   * ユーザーのログイン処理を実行
   * 認証成功時はユーザー情報を取得し、ログイン状態を更新する
   */
  const login = useCallback(async (username: string, password: string) => {
    try {
      await authLogin(username, password)
      setIsLoggedIn(true)
      await refreshUserInfo()
    } catch (error) {
      handleError('LOGIN_ERROR')
      throw error
    }
  }, [refreshUserInfo, handleError])

  /**
   * ユーザーのログアウト処理を実行
   * トークンを削除し、認証状態とユーザー情報をクリアする
   */
  const logout = useCallback(() => {
    try {
      authLogout()
      setIsLoggedIn(false)
      clearUser()
    } catch {
      handleError('UNKNOWN_ERROR')
    }
  }, [clearUser, handleError])

  // 初期化時に認証状態を確認
  useEffect(() => {
    checkAuth()
  }, [checkAuth])

  // タブがアクティブに戻った時に認証状態を再確認
  useEffect(() => {
    const handleVisibilityChange = () => {
      if (document.visibilityState === 'visible') {
        checkAuth()
      }
    }
    document.addEventListener('visibilitychange', handleVisibilityChange)
    return () => {
      document.removeEventListener('visibilitychange', handleVisibilityChange)
    }
  }, [checkAuth])

  // コンテキスト値のメモ化
  const value = useMemo(() => ({
    isLoggedIn,
    login,
    logout,
    checkAuth,
  }), [isLoggedIn, login, logout, checkAuth])

  return (
    <AuthContext.Provider value={value}>
      {children}
    </AuthContext.Provider>
  )
}
